import { Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { AddEditKeywordModal } from "../modal/add-edit";
import KeywordDeleteModal from "../modal/delete";

import type { DataGridColumn } from "@/components/kit";

import { DataTable, RowMenu, SearchBox } from "@/components/kit";
import { Toolbar } from "@/components/sales/shared";
import { Keyword } from "@/types/keyword";

interface KeywordListTableProps {
  items: Keyword[];
  page: number;
  totalKeywords: number;
  changePage: (page: number) => void;
  isAdding: boolean;
  onCloseAdd: () => void;
  isLoading: boolean;
  limit: number;
  onLimitChange: (limit: number) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
}

export const KeywordListTable = ({
  items,
  page,
  totalKeywords,
  changePage,
  isAdding,
  onCloseAdd,
  isLoading,
  limit,
  onLimitChange,
  searchTerm,
  onSearchChange,
}: KeywordListTableProps) => {
  const [editing, setEditing] = useState<Keyword | null>(null);
  const [deleting, setDeleting] = useState<Keyword | null>(null);

  const columns = useMemo<DataGridColumn<Keyword>[]>(
    () => [
      {
        id: "keyword",
        header: "Keyword",
        isRowHeader: true,
        cell: (item) => <span className="font-medium">{item.keyword}</span>,
      },
      {
        id: "products",
        header: "Products",
        align: "end",
        width: 120,
        cell: (item) => (
          <span className="tabular-nums">
            {item.products.length}
            <span className="sr-only"> products</span>
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 64,
        cell: (item) => (
          <RowMenu
            items={[
              {
                id: "edit",
                label: "Edit",
                icon: <Pencil className="size-4" />,
                onAction: () => setEditing(item),
              },
              {
                id: "delete",
                label: "Delete",
                icon: <Trash2 className="size-4" />,
                danger: true,
                onAction: () => setDeleting(item),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar>
        <SearchBox
          className="w-80"
          placeholder="Search keywords"
          value={searchTerm}
          onChange={onSearchChange}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        empty={{
          title: "No keywords found",
          description: "Try a different search, or add a keyword.",
        }}
        getRowId={(item) => item.id}
        isLoading={isLoading}
        label="Keywords"
        pagination={{
          page,
          pageSize: limit,
          total: totalKeywords,
          onPageChange: changePage,
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
      />

      <AddEditKeywordModal
        isEdit={false}
        isOpen={isAdding}
        onClose={onCloseAdd}
      />
      {editing ? (
        <AddEditKeywordModal
          isEdit
          isOpen
          keyword={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <KeywordDeleteModal
          isOpen
          keyword={deleting}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};
